import { useEffect, useState } from 'react';
import { DateRangeInput } from '../common/DateRangeInput';

import { useProjectUsers } from '../../hooks/useProjectUsers';
import { useDebounce } from '../../utils/useDebounce';
import type { DocumentAuditAction, DocumentDownloadLogFilters } from '../../api/downloadLogs';

const ACTION_OPTIONS: { value: DocumentAuditAction; label: string }[] = [
  { value: 'download', label: 'Downloaded' },
  { value: 'created', label: 'Created' },
  { value: 'updated', label: 'Updated' },
  { value: 'deleted', label: 'Deleted' },
];

interface DownloadLogFiltersBarProps {
  scope: 'project' | 'global';
  projectId?: string;
  /** Seeds the initial field values once (e.g. from persisted storage); not a controlled value. */
  initialFilters?: DocumentDownloadLogFilters;
  onChange: (filters: DocumentDownloadLogFilters) => void;
}

const toDateOnly = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/**
 * Date-range, action, user, document-name, file-type and file-size filters.
 *
 * The user filter is a project-member dropdown (backend's `user_id` filter is
 * an exact UUID match, not a name/email search) — project-scoped only, since
 * no global user-list endpoint exists for the admin scope.
 */
export const DownloadLogFiltersBar = ({ scope, projectId, initialFilters, onChange }: DownloadLogFiltersBarProps) => {
  const [dateRange, setDateRange] = useState<[Date, Date] | null>(() =>
    initialFilters?.date_from && initialFilters?.date_to
      ? [new Date(initialFilters.date_from), new Date(initialFilters.date_to)]
      : null,
  );
  const [action, setAction] = useState(() => initialFilters?.action ?? '');
  const [documentName, setDocumentName] = useState(() => initialFilters?.document_name ?? '');
  const [contentType, setContentType] = useState(() => initialFilters?.content_type ?? '');
  const [userId, setUserId] = useState(() => initialFilters?.user_id ?? '');
  const [fileSizeMin, setFileSizeMin] = useState(() => initialFilters?.file_size_min?.toString() ?? '');
  const [fileSizeMax, setFileSizeMax] = useState(() => initialFilters?.file_size_max?.toString() ?? '');

  const debouncedDocumentName = useDebounce(documentName, 300);
  const debouncedFileSizeMin = useDebounce(fileSizeMin, 300);
  const debouncedFileSizeMax = useDebounce(fileSizeMax, 300);

  const { users } = useProjectUsers(scope === 'project' ? projectId || '' : '');

  // Drop a restored `userId` that no longer matches a current team member
  // (e.g. the member left the project since the filter was persisted) —
  // otherwise the list would silently filter to nobody with no visible cause.
  // Only fires for a stale restored value; a fresh manual selection is always
  // chosen from `users`, so it can never mismatch.
  useEffect(() => {
    if (!userId || users.length === 0) return;
    if (!users.some(u => u.user_id === userId)) {
      setUserId('');
      emit({ user_id: undefined });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users]);

  const emit = (overrides: Partial<DocumentDownloadLogFilters> = {}) => {
    const filters: DocumentDownloadLogFilters = {
      action: (action || undefined) as DocumentAuditAction | undefined,
      document_name: debouncedDocumentName || undefined,
      content_type: contentType || undefined,
      user_id: userId || undefined,
      file_size_min: debouncedFileSizeMin ? Number(debouncedFileSizeMin) : undefined,
      file_size_max: debouncedFileSizeMax ? Number(debouncedFileSizeMax) : undefined,
      date_from: dateRange ? toDateOnly(dateRange[0]) : undefined,
      date_to: dateRange ? toDateOnly(dateRange[1]) : undefined,
      ...overrides,
    };
    onChange(filters);
  };

  return (
    <div className="mb-4 flex flex-wrap items-end gap-3">
      <div>
        <label className="mb-1 block text-xs text-[#888]">Date range</label>
        <DateRangeInput
          value={dateRange}
          onChange={next => {
            setDateRange(next);
            emit({
              date_from: next ? toDateOnly(next[0]) : undefined,
              date_to: next ? toDateOnly(next[1]) : undefined,
            });
          }}
        />
      </div>

      <div>
        <label className="mb-1 block text-xs text-[#888]">Action</label>
        <select
          value={action}
          onChange={e => {
            const next = e.target.value as DocumentAuditAction | '';
            setAction(next);
            emit({ action: (next || undefined) as DocumentAuditAction | undefined });
          }}
          className="rounded-md border border-[#eaeaea] px-2 py-1.5 text-sm"
        >
          <option value="">All actions</option>
          {ACTION_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {scope === 'project' && (
        <div>
          <label className="mb-1 block text-xs text-[#888]">User</label>
          <select
            value={userId}
            onChange={e => {
              setUserId(e.target.value);
              emit({ user_id: e.target.value || undefined });
            }}
            className="rounded-md border border-[#eaeaea] px-2 py-1.5 text-sm"
          >
            <option value="">All users</option>
            {users.map(u => (
              <option key={u.user_id} value={u.user_id}>
                {u.user.first_name} {u.user.last_name} ({u.user.email})
              </option>
            ))}
          </select>
        </div>
      )}

      <div>
        <label className="mb-1 block text-xs text-[#888]">Document name</label>
        <input
          type="text"
          value={documentName}
          onChange={e => setDocumentName(e.target.value)}
          onBlur={() => emit()}
          placeholder="Search…"
          className="rounded-md border border-[#eaeaea] px-2 py-1.5 text-sm"
        />
      </div>

      <div>
        <label className="mb-1 block text-xs text-[#888]">File type</label>
        <input
          type="text"
          value={contentType}
          onChange={e => {
            setContentType(e.target.value);
          }}
          onBlur={() => emit()}
          placeholder="e.g. application/pdf"
          className="rounded-md border border-[#eaeaea] px-2 py-1.5 text-sm"
        />
      </div>

      <div>
        <label className="mb-1 block text-xs text-[#888]">Min size (bytes)</label>
        <input
          type="number"
          min={0}
          value={fileSizeMin}
          onChange={e => setFileSizeMin(e.target.value)}
          onBlur={() => emit()}
          className="w-28 rounded-md border border-[#eaeaea] px-2 py-1.5 text-sm"
        />
      </div>

      <div>
        <label className="mb-1 block text-xs text-[#888]">Max size (bytes)</label>
        <input
          type="number"
          min={0}
          value={fileSizeMax}
          onChange={e => setFileSizeMax(e.target.value)}
          onBlur={() => emit()}
          className="w-28 rounded-md border border-[#eaeaea] px-2 py-1.5 text-sm"
        />
      </div>
    </div>
  );
};
